/**
 * Kart rengi serbest seçilir ve hex olarak saklanır. Sabit palet gerekmiyor: renk zemine
 * yalnızca `--sso-card-tint-bg` oranında (açık temada %7, koyuda %13) karıştırılıyor, yani
 * sonuç her zaman yüzey rengine yakın kalıyor ve metin kontrastı seçilen renkten
 * bağımsız olarak korunuyor.
 */

export interface Hsv {
  h: number;
  s: number;
  v: number;
}

const HEX = /^#[0-9a-f]{6}$/i;

/** Backend de aynı kontrolü yapıyor; buradaki değer bir CSS custom property'sine yazılıyor. */
export function normalizeColor(value: string | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  return HEX.test(trimmed) ? trimmed.toLowerCase() : null;
}

function toHex(value: number): string {
  const clamped = Math.max(0, Math.min(255, Math.round(value)));
  return clamped.toString(16).padStart(2, "0");
}

export function hsvToHex({ h, s, v }: Hsv): string {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;

  const sector = Math.floor(((h % 360) + 360) % 360 / 60);
  const rgb = [
    [c, x, 0],
    [x, c, 0],
    [0, c, x],
    [0, x, c],
    [x, 0, c],
    [c, 0, x],
  ][sector];

  return "#" + rgb.map((channel) => toHex((channel + m) * 255)).join("");
}

export function hexToHsv(hex: string): Hsv {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0;
  if (delta !== 0) {
    if (max === r) h = ((g - b) / delta) % 6;
    else if (max === g) h = (b - r) / delta + 2;
    else h = (r - g) / delta + 4;
    h *= 60;
    if (h < 0) h += 360;
  }

  return { h, s: max === 0 ? 0 : delta / max, v: max };
}

/** Seçici ilk açıldığında bir yerden başlamalı; renksiz kartta orta doygunlukta bir mavi. */
export const DEFAULT_PICKER_HSV: Hsv = { h: 214, s: 0.65, v: 0.85 };
